import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import debounce from 'lodash/debounce';

import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs/inputText';
import { spacing, Subtitle2 } from 'ui/theme';
import { useTranslations } from 'modules/creativeLibrary/hooks/useTranslations';
import { useUploadAssetsContext } from 'modules/creativeLibrary/contexts/UploadAssetsContext';

const InputContainer = styled.div`
  margin-top: ${spacing.half};
  width: 100%;
  max-width: 500px;
`;

const isValidUrl = (url: string): boolean => {
  try {
    // Only allow HTTP/HTTPS protocols
    return ['http:', 'https:'].includes(new URL(url).protocol);
  } catch {
    return false;
  }
};

const urlToFile = async (url: string): Promise<File> => {
  if (!isValidUrl(url)) {
    throw new Error('Invalid URL protocol. Only HTTP and HTTPS are allowed.');
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.statusText}`);
  }

  const blob = await response.blob();

  // Extract filename from URL or use a default
  const urlParts = url.split('/');
  const lastPart = urlParts[urlParts.length - 1];
  const filename = lastPart.includes('.')
    ? lastPart
    : `image_${Date.now()}.${blob.type?.split('/')[1] || 'png'}`;

  return new File([blob], filename, {
    type: blob.type,
    lastModified: Date.now(),
  });
};

type UrlToFileInputProps = {
  /** Test ID for the input field */
  inputTestId?: string;
};

export const UrlToFileInput = ({ inputTestId }: UrlToFileInputProps) => {
  const { t } = useTranslations();
  const { handleNewFiles } = useUploadAssetsContext();
  const [inputUrl, setInputUrl] = useState<string>('');
  const [urlIsValid, setUrlIsValid] = useState<boolean>(true);
  const [urlIsValidating, setUrlIsValidating] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<'protocol' | 'image' | null>(null);
  const imageValidator = useRef<HTMLImageElement>();

  const handleUrlToFile = useCallback(
    async (url: string) => {
      try {
        const file = await urlToFile(url);
        if (file) {
          handleNewFiles([file]);
          setInputUrl(''); // Clear the input after successful addition
        }
      } catch (error) {
        setUrlIsValid(false);
        setUrlIsValidating(false);
        setErrorType('image');
      }
    },
    [handleNewFiles],
  );

  const handleImageValidation = useCallback(
    async (url: string) => {
      // Check URL validity first
      if (!isValidUrl(url)) {
        setUrlIsValid(false);
        setUrlIsValidating(false);
        setErrorType('protocol');
        return;
      }

      // Use the same validation approach as the panel UrlInput
      imageValidator.current ??= new Image();

      imageValidator.current.onload = () => {
        setUrlIsValid(true);
        setUrlIsValidating(false);
        // Convert URL to file and add to pending uploads
        handleUrlToFile(url);
      };

      imageValidator.current.onerror = () => {
        setUrlIsValid(false);
        setUrlIsValidating(false);
        setErrorType('image');
      };

      imageValidator.current.src = url;
    },
    [handleUrlToFile],
  );

  const handleInputUrlChange = useCallback(debounce(handleImageValidation, 500), [
    handleImageValidation,
  ]);

  const getWarningMessage = () => {
    if (!urlIsValid) {
      return errorType === 'protocol'
        ? t('errors.remoteUrl.invalidUrlProtocol')
        : t('errors.remoteUrl.invalidImage');
    }
    return undefined;
  };

  return (
    <>
      <Subtitle2>{t('label.imageUrl')}</Subtitle2>
      <InputContainer>
        <FormField hasWarning={!urlIsValid} inputId={inputTestId} warningText={getWarningMessage()}>
          <InputText
            data-test={inputTestId}
            id={inputTestId}
            isLoading={urlIsValidating}
            onChange={(value: string) => {
              setInputUrl(value);
              setUrlIsValidating(true);
              handleInputUrlChange(value);
            }}
            placeholder={t('input.url.placeholder')}
            value={inputUrl}
          />
        </FormField>
      </InputContainer>
    </>
  );
};
